import PropTypes from 'prop-types';
import useProposalDesignations from 'apps/proposals/hooks/use-proposal-designations';
import { useProposalFormContext } from '../context/proposal-form-context';
import { AdvancedCombobox, NumericInput, Button, Icon } from '@evertrue/et-components';

const DesignationsField = ({ onChange, onBlur, name = '', value = [], required = false, field, meta, helpers }) => {
  const { designationLimit, standardFieldsConfig = {}, isDesignationInDollars } = useProposalFormContext();
  const { require_pairs = {} } = standardFieldsConfig;
  const [designations = []] = useProposalDesignations();

  const proposalHasDesignation = (designation) => {
    return value.find((propDesig) => propDesig.ugc_designation_id === designation.value);
  };

  const remainingDesignations = designations.reduce((result, designation) => {
    if (proposalHasDesignation(designation)) return result;

    return result.concat({
      value: designation.value,
      label: designation.label,
    });
  }, []);

  const handleRemoveDesignation = (designation) => {
    const designations = value.filter(
      (proposalDesig) => proposalDesig.ugc_designation_id !== designation.ugc_designation_id
    );
    helpers.setValue(designations);
  };

  const handleAddDesignation = (designation) => {
    const designations = [...value, { ugc_designation_id: designation.value, name: designation.label, value: '' }];
    helpers.setValue(designations);
  };

  const handleEditDesignation = (designation, newVal = []) => {
    const designations = value.map((proposalDesig) => {
      if (proposalDesig.ugc_designation_id === designation.ugc_designation_id) {
        return { ...designation, value: newVal };
      }

      return proposalDesig;
    });

    helpers.setValue(designations);
  };

  return (
    <ul>
      {(value || []).map((designation) => (
        <li key={designation.ugc_designation_id} className="proposal-form--list-row">
          <div
            className={
              require_pairs.designations
                ? 'proposal-form--designation-column proposal-form--designation-column-focus'
                : 'proposal-form--designation-column'
            }
          >
            <strong>{designation.name}</strong>
          </div>
          {require_pairs.designations && <hr data-testid="proposal-hr" />}
          <div className="proposal-form--designation-column">
            {isDesignationInDollars ? (
              <NumericInput
                className={
                  require_pairs.designations
                    ? 'proposal-form--form-field-amount proposal-form--form-field-amount-focus'
                    : 'proposal-form--form-field-amount'
                }
                aria-label={`Designation amount for ${designation.name}`}
                placeholder={0}
                value={designation.value}
                maskType="currency"
                integerLimit={10}
                allowDecimal={true}
                onChange={(value) => {
                  handleEditDesignation(designation, value);
                }}
                showIcon={true}
              />
            ) : (
              <div className="proposal-form--form-field-percent">
                <NumericInput
                  aria-label={`Designation percent for ${designation.name}`}
                  value={designation.value}
                  placeholder={0}
                  maskType="percent"
                  allowDecimal={true}
                  valueLimit={100}
                  integerLimit={3}
                  onChange={(value) => {
                    handleEditDesignation(designation, value);
                  }}
                  showIcon={true}
                  className={require_pairs.designations ? 'proposal-form--form-field-amount-focus' : ''}
                />
              </div>
            )}
            <Button
              type="simple"
              onClick={() => handleRemoveDesignation(designation)}
              label={`Remove designation, ${designation.name}`}
            >
              <Icon
                icon="cancel"
                className="proposal-form--cancel"
                title={`Remove designation, ${designation.name}`}
                size={1}
              />
            </Button>
          </div>
        </li>
      ))}
      <li>
        <AdvancedCombobox
          label={`Select designations to add to proposal up to a max of ${designationLimit}`}
          disabled={!remainingDesignations.length || value?.length >= designationLimit}
          options={remainingDesignations}
          placeholder="Select a designation to add..."
          onChange={(designation) => handleAddDesignation(designation)}
          searchable={remainingDesignations.length > 6}
        />
      </li>
    </ul>
  );
};

DesignationsField.propTypes = {
  onChange: PropTypes.func,
  onBlur: PropTypes.func,
  name: PropTypes.string,
  value: PropTypes.array,
  required: PropTypes.bool,
  field: PropTypes.shape({
    name: PropTypes.string,
    onBlur: PropTypes.func,
    onChange: PropTypes.func,
    value: PropTypes.any,
    checked: PropTypes.bool,
    multiple: PropTypes.bool,
  }),
  meta: PropTypes.shape({
    error: PropTypes.string,
    initialError: PropTypes.string,
    initialTouched: PropTypes.bool,
    initialValue: PropTypes.any,
    touched: PropTypes.bool,
    value: PropTypes.any,
  }),
  helpers: PropTypes.shape({
    setValue: PropTypes.func,
    setTouched: PropTypes.func,
    setError: PropTypes.func,
  }),
};

export default DesignationsField;
